import React, { useState, useMemo } from "react";
import { useField } from "formik";
import { Flex, Text, Label } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { Tab } from "../../types";
import * as styles from "./styles_vanilla.css";

export type TabNameSelectorProps = {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  "data-testid"?: string;
  tabs?: Tab[];
};

const TabNameSelector: React.FC<TabNameSelectorProps> = ({
  name,
  label,
  placeholder = "Enter tab name (optional)",
  disabled = false,
  "data-testid": testId,
  tabs = [],
}) => {
  const [field, _, helpers] = useField<string>(name);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const existingTabNames = useMemo(() => {
    if (!tabs || !Array.isArray(tabs)) return [];

    return tabs.map((tab) => tab.name);
  }, [tabs]);

  const filteredSuggestions = useMemo(() => {
    const currentValue = field.value || "";
    if (!currentValue.trim()) return existingTabNames;

    return existingTabNames.filter((tabName) =>
      tabName.toLowerCase().includes(currentValue.toLowerCase()),
    );
  }, [existingTabNames, field.value]);

  const handleInputFocus = () => {
    setShowSuggestions(existingTabNames.length > 0);
  };

  const handleInputBlur = () => {
    setShowSuggestions(false);
  };

  const handleSuggestionMouseDown = async (tabName: string) => {
    await helpers.setValue(tabName);
    setShowSuggestions(false);
  };

  return (
    <Flex flexDirection="column" gap={SPACING.SMALL}>
      <Flex flexDirection="column" className={styles.container}>
        <Label>{label}</Label>

        <FormTextInput
          name={name}
          placeholder={placeholder}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={disabled}
          maxLength={50}
          isFullWidth
          data-testid={testId}
        />

        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <ul className={styles.suggestionsDropdown} data-testid={`${testId}-suggestions`}>
            {filteredSuggestions.map((tabName) => (
              <li
                className={styles.suggestionItem}
                key={tabName}
                data-testid={`${testId}-suggestion-${tabName}`}
              >
                <button
                  className={styles.suggestionButton}
                  type="button"
                  onMouseDown={() => handleSuggestionMouseDown(tabName)}
                >
                  {tabName}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Flex>

      <Text variant="regular-sm">
        Start typing to see existing tabs or enter a new tab name
      </Text>
    </Flex>
  );
};

export default TabNameSelector;
